import { useQuery } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'helpers/reactUseHelper';
import {
    ColDef,
    ColGroupDef,
    ColumnState,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { debounce } from 'lodash';
import { ApT } from './types';
import { BudgetExpenseOrIncome } from 'business/constants';

/** Hook to fetch all the aps lines.  */
export const useApsQuery = ({
    year,
    expenseOrIncome,
    enabled,
}: {
    year: number;
    expenseOrIncome: BudgetExpenseOrIncome;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: ['allApsView', expenseOrIncome.toUpperCase(), year],

        queryFn: async () => {
            const response = await RQAPICallWithData<ApT[]>({
                endpoint: `budget_overview/aps/${expenseOrIncome.toLowerCase()}?year=${year}`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled,
        refetchOnWindowFocus: false,
    });

const DEBOUNCE_TIME = 1000;

type ExtraProps = {
    getRowId: (params: GetRowIdParams) => string;
    onGridReady: (params: GridReadyEvent) => void;
    onColumnPinned: () => void;
    onColumnMoved: () => void;
    onColumnResized: () => void;
    onSortChanged: () => void;
    onGridColumnsChanged: () => void;
};

/**
 *  this hooks is responsible for synchronizing columnDef and expanded rows from ag-grid with localstorage
 *  should work with any ag-grid columnDef structure
 *  */
export const useLocalStorageAGGridProps = (
    cacheKey: string,
    getRowId: (params: GetRowIdParams) => string,
    columns: (ColDef | ColGroupDef)[],
): [(ColDef | ColGroupDef)[], ExtraProps] => {
    const isColGroupDefT = (
        variableToCheck: unknown,
    ): variableToCheck is ColGroupDef =>
        (variableToCheck as ColGroupDef)?.children !== undefined;

    const [storedSettings, setStoredSettings] = useLocalStorage<{
        colState: ColumnState[];
    }>(cacheKey, {
        colState: [],
    });
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    // TODO StrictNullCheck : Try to remove "!"
    const previousColState = useRef(storedSettings!.colState);
    const applyStoredColdefs = (
        coldefs: (ColDef | ColGroupDef)[],
        storedColDefs: { colId: string }[],
    ) => {
        const updatedColDefs: (ColDef | ColGroupDef)[] = coldefs.map((col) => {
            if (isColGroupDefT(col)) {
                return {
                    ...col,
                    children: applyStoredColdefs(col.children, storedColDefs),
                };
            }
            return {
                ...col,
                ...storedColDefs.find(
                    (storedCol) => storedCol.colId === col.field,
                ),
            };
        });
        return updatedColDefs;
    };

    const columnDefs = useMemo(
        // TODO StrictNullCheck : Try to remove "!"
        () => applyStoredColdefs(columns, storedSettings!.colState),
        [columns, previousColState],
    );

    const onGridReady = (params: { api: GridApi }) => {
        setGridApi(params.api);
    };

    const onColumnChanged = useCallback(() => {
        if (gridApi === null) return;
        const newColState = gridApi.getColumnState();
        if (newColState !== undefined && newColState.length > 0) {
            setStoredSettings({
                colState: newColState,
            });
        }
    }, [storedSettings, setStoredSettings, gridApi]);

    const debouncedOnColumnChanged = useCallback(
        debounce(onColumnChanged, DEBOUNCE_TIME),
        [onColumnChanged],
    );

    return [
        columnDefs,
        {
            getRowId,
            onGridReady,
            onColumnPinned: debouncedOnColumnChanged,
            onColumnMoved: debouncedOnColumnChanged,
            onColumnResized: debouncedOnColumnChanged,
            onSortChanged: debouncedOnColumnChanged,
            onGridColumnsChanged: debouncedOnColumnChanged,
        },
    ];
};
